import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import Navbar from '../components/Navbar';

const DASHBOARD_SIGN_IN_PATH = '/authentication/sign-in';
const REDIRECT_DELAY_SEC = 5;

function getDashboardUrl() {
  const base = process.env.REACT_APP_DASHBOARD_URL || '';
  return base ? base.replace(/\/$/, '') : null;
}

function getDashboardSignInUrl() {
  const base = getDashboardUrl();
  return base ? `${base}${DASHBOARD_SIGN_IN_PATH}` : null;
}

function getClientSiteUrl() {
  const base = process.env.REACT_APP_FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  return base ? base.replace(/\/$/, '') : null;
}

const VerifyEmail = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [verifiedAs, setVerifiedAs] = useState(null); // 'client' | 'property_owner'
  const [redirectCountdown, setRedirectCountdown] = useState(REDIRECT_DELAY_SEC);
  const redirectTriggered = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Verification link is invalid. Missing token.');
      return;
    }
    const verify = async () => {
      try {
        const response = await api.get(`/api/clients/verify/${token}`);
        setMessage(response.data?.message || 'Email verified successfully.');
        setVerifiedAs('client');
        setStatus('success');
        return;
      } catch (clientErr) {
        if (clientErr.response?.status === 400 || clientErr.response?.status === 404) {
          try {
            await api.get(`/api/property-owner/verify/${token}`);
            setMessage('Your email has been verified successfully.');
            setVerifiedAs('property_owner');
            setStatus('success');
            return;
          } catch (ownerErr) {
            const msg = ownerErr.response?.data?.message || ownerErr.message;
            setMessage(msg || 'This link is invalid, expired, or has already been used.');
            setStatus('error');
            return;
          }
        }
        const msg = clientErr.response?.data?.message || clientErr.message;
        setMessage(clientErr.response?.status === 400
          ? (msg || 'This link is invalid, expired, or has already been used.')
          : (msg || 'Verification failed. Please try again or request a new link.'));
        setStatus('error');
      }
    };
    verify();
  }, [token]);

  // Auto-redirect after success using env URLs
  useEffect(() => {
    if (status !== 'success' || verifiedAs == null || redirectTriggered.current) return;

    const dashboardSignIn = getDashboardSignInUrl();
    const clientBase = getClientSiteUrl();
    const redirectUrl = verifiedAs === 'property_owner'
      ? dashboardSignIn
      : (clientBase ? `${clientBase}/login` : null);

    if (!redirectUrl) {
      setRedirectCountdown(-1);
      return;
    }

    const tick = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(tick);
          if (!redirectTriggered.current) {
            redirectTriggered.current = true;
            window.location.href = redirectUrl;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [status, verifiedAs]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
      <Navbar />
      <div className="flex items-center justify-center min-h-screen pt-20 pb-8 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-4">
            <div className="flex justify-center gap-3 mb-4">
              <div className="flex items-center justify-center p-1.5 rounded-lg" style={{ backgroundColor: '#FFF' }}>
                <img src="/hg-logo.png" alt="Logo" className="w-auto" style={{ maxHeight: '26px' }} />
              </div>
              <div className="flex flex-col text-left">
                <span className="text-2xl font-bold uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>HOSTA GLOBAL</span>
                <span className="text-[10px] uppercase" style={{ color: '#D5B577', opacity: 0.8 }}>ELEVATING ASSETS | REDEFINING STAYS</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Email verification</h1>
          </div>
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border p-6" style={{ borderColor: 'rgba(213,181,119,0.3)' }}>
            {status === 'loading' && (
              <div className="text-center py-6">
                <div className="w-10 h-10 border-2 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#D5B577', borderTopColor: 'transparent' }} />
                <p className="text-gray-300 text-sm">Please wait...</p>
              </div>
            )}
            {status === 'success' && (
              <div className="text-center py-2">
                <div className="mb-4 p-4 rounded-xl bg-green-500/20 border border-green-500/50 text-green-200 text-sm">{message}</div>
                {verifiedAs === 'property_owner' && (
                  <p className="text-gray-300 text-sm mb-2">You can now sign in to the partner dashboard.</p>
                )}
                {verifiedAs === 'client' && (
                  <p className="text-gray-300 text-sm mb-2">You can now sign in to your account.</p>
                )}
                {redirectCountdown > 0 && (
                  <p className="text-gray-400 text-sm mb-4">
                    Redirecting in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}…
                  </p>
                )}
                {redirectCountdown < 0 && (
                  <p className="text-gray-400 text-sm mb-4">Set REACT_APP_DASHBOARD_URL or REACT_APP_FRONTEND_URL in your .env to enable redirect.</p>
                )}
                {verifiedAs === 'property_owner' ? (
                  (() => {
                    const url = getDashboardSignInUrl();
                    return url ? (
                      <a href={url} className="block w-full py-3 rounded-lg font-semibold text-center" style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}>Go to partner dashboard</a>
                    ) : (
                      <p className="text-gray-400 text-sm">Set REACT_APP_DASHBOARD_URL (e.g. https://admin.hostaglobal.com) in .env.</p>
                    );
                  })()
                ) : (
                  (() => {
                    const url = getClientSiteUrl();
                    return url ? (
                      <a href={`${url}/login`} className="block w-full py-3 rounded-lg font-semibold text-center" style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}>Go to sign in</a>
                    ) : (
                      <Link to="/login" className="block w-full py-3 rounded-lg font-semibold text-center" style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}>Go to sign in</Link>
                    );
                  })()
                )}
              </div>
            )}
            {status === 'error' && (
              <div className="text-center py-2">
                <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-sm">{message}</div>
                <p className="text-gray-300 text-sm mb-6">This link may have already been used or expired. Try signing in or register again.</p>
                <div className="flex flex-col gap-3">
                  <Link to="/login" className="block w-full py-3 rounded-lg font-semibold text-center" style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}>Go to Sign in (Guest)</Link>
                  {getDashboardSignInUrl() && (
                    <a href={getDashboardSignInUrl()} className="block w-full py-2.5 rounded-lg font-medium text-center text-sm border" style={{ borderColor: 'rgba(213,181,119,0.5)', color: '#D5B577' }}>Go to Sign in (Partner)</a>
                  )}
                  <Link to="/register" className="block w-full py-2.5 rounded-lg font-medium text-center text-sm border" style={{ borderColor: 'rgba(213,181,119,0.5)', color: '#D5B577' }}>Register again</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
